'use client';

import { AlertTriangle, FileText, Pin, PinOff, RotateCcw, Search, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { Sensitivity } from '@/lib/protobuf/gen/common/sensitivity_pb';
import { SensitivityToggle } from '@/features/knowledge/SensitivityToggle';
import { ManualActionBar } from '@/features/workflows/components/ManualActionBar';
import { ConversionProgressBar } from './ConversionProgressBar';
import { ConverterBadge, isDoclingRetryLive } from './ConverterBadge';
import {
  CONVERSION_EMPTY_STATUS,
  isActionableFailure,
  isConversionEmpty,
  offersIndexSkippedRetry,
} from './documentStatus';
import { useDocumentsStore } from './documentsStore';
import { DocumentActionsMenu } from './DocumentActionsMenu';
import { DocumentViewerModal } from './DocumentViewerModal';

function formatBytes(bytes: bigint): string {
  const num = Number(bytes);
  if (num === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(num) / Math.log(1024));
  const value = num / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(timestamp: { seconds: bigint } | undefined): string {
  if (!timestamp) return '';
  const ms = Number(timestamp.seconds) * 1000;
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function indexingStatusVariant(
  status: string,
): 'default' | 'running' | 'completed' | 'failed' | 'queued' | 'warning' {
  switch (status.toLowerCase()) {
    case 'indexed':
    case 'complete':
    case 'completed':
      return 'completed';
    case 'uploading':
    case 'indexing':
    case 'processing':
      return 'running';
    case 'failed':
    case 'error':
      return 'failed';
    // `.toLowerCase()` on the LABEL, not a hand-lowercased literal: the switch
    // subject is lowercased above, so a case-exact label would never match and
    // would fall to `default` SILENTLY — a grey badge, indistinguishable from
    // "we forgot". A `case` label may be any expression, so the constant can be
    // the single source of truth here too.
    case CONVERSION_EMPTY_STATUS.toLowerCase():
      return 'warning';
    case 'pending':
    case 'queued':
      return 'queued';
    default:
      return 'default';
  }
}

/**
 * Human label for a wire status.
 *
 * There is no status -> label map for any OTHER status: every one of them still
 * renders its raw camelCase identifier, which is why a user today literally sees
 * `convertingHiFi` in the badge. That is a pre-existing gap (tracked separately);
 * what this function guarantees is that the empty-conversion status is never one
 * of them, in any locale.
 *
 * This used to guard with `t.has()` and carry a hard-coded English fallback,
 * against a stated premise that "next-intl renders the KEY PATH when a key is
 * missing". That premise is FALSE IN THIS APP, and Stage 6c caught it:
 * `components/i18n/I18nProvider.tsx` passes a `getMessageFallback` that walks the
 * statically imported `en.json` and returns the English string, so a missing key
 * renders the English string and never the key path. (This quoted the literal
 * "No text found" until BE-15 reworded that key to "Last conversion: no text".
 * A comment naming a value it does not own goes stale on the very commit that
 * changes it — this repo's most recurrent defect family, one line away.)
 * A second mechanism makes the
 * input state unreachable anyway — `lib/i18n/__tests__/catalogs.test.ts` is a
 * CI-blocking key-set parity gate, so a lagging catalogue cannot reach master.
 *
 * The `t.has()` shape was borrowed from `app/auth/magic-link/verify/page.tsx`,
 * where the key is DYNAMIC and unbounded (`authErrors.${err}`, `err` from the
 * server) and no catalogue gate can cover it. Borrowing it for a static key kept
 * the shape and dropped the asymmetry that justified it — and the fallback string
 * it required put a second copy of an English user-facing string in TSX, where
 * neither the parity gate nor a translator can see it.
 */
const STATUS_LABEL_KEYS: Record<string, string> = {
  [CONVERSION_EMPTY_STATUS.toLowerCase()]: 'documentList.statusConversionEmpty',
};

function indexingStatusLabel(
  t: ReturnType<typeof useTranslations>,
  status: string,
): string {
  const key = STATUS_LABEL_KEYS[status.toLowerCase()];
  return key ? t(key) : status;
}

interface DocumentListProps {
  collectionId?: string;
  className?: string;
  activeTags?: string[];
  /** True when a scope is selected but resolves to no tags — show nothing. */
  emptyScopeActive?: boolean;
}

function DocumentList({ collectionId, className, activeTags, emptyScopeActive }: DocumentListProps) {
  const t = useTranslations('documents');
  const documents = useDocumentsStore((s) => s.documents);
  const loadState = useDocumentsStore((s) => s.loadState);
  const listDocuments = useDocumentsStore((s) => s.actions.listDocuments);
  const deleteDocument = useDocumentsStore((s) => s.actions.deleteDocument);
  const updateDocumentPinned = useDocumentsStore((s) => s.actions.updateDocumentPinned);
  const updateDocumentSensitivity = useDocumentsStore(
    (s) => s.actions.updateDocumentSensitivity,
  );
  const retryDocument = useDocumentsStore((s) => s.actions.retryDocument);
  const retryDocling = useDocumentsStore((s) => s.actions.retryDocling);
  const openDocumentViewer = useDocumentsStore((s) => s.actions.openDocumentViewer);
  const conversionProgress = useDocumentsStore((s) => s.conversionProgress);
  const knownTags = useDocumentTags();

  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    listDocuments(collectionId);
  }, [collectionId, listDocuments]);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  // Search filters the in-memory list by name (title or filename), combined with
  // any active tag/scope filter. Local + instant — the backend document-search
  // path is not implemented, and name matching is what the search box implies.
  const filteredDocuments = React.useMemo(() => {
    // A selected scope with no resolved tags contains nothing — never fall back
    // to the unfiltered list (which would make the scope look like it holds
    // every document).
    if (emptyScopeActive) return [];
    const q = searchQuery.trim().toLowerCase();
    let list = documents;
    if (activeTags && activeTags.length > 0) {
      list = list.filter((doc) => doc.tags && doc.tags.some((tag) => activeTags.includes(tag)));
    }
    if (q) {
      list = list.filter(
        (doc) =>
          (doc.title || '').toLowerCase().includes(q) ||
          (doc.filename || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [documents, activeTags, searchQuery, emptyScopeActive]);

  const isSearching = searchQuery.trim().length > 0;
  const isLoading = loadState.status === 'loading';

  // A load that failed is NOT an empty library. Without this branch 'error'
  // fell through to the "Your library is empty" copy below — a false statement
  // about the user's data, and on the read-only /demo account a false statement
  // about the product (a curated library described as empty).
  if (loadState.status === 'error') {
    return (
      <div className={cn('flex flex-col items-center gap-3 py-16', className)}>
        <AlertTriangle className="h-12 w-12 text-warning" />
        <p className="max-w-md text-center text-sm text-muted-foreground">
          {t('documentList.loadFailed')}
        </p>
        <Button variant="secondary" size="sm" onClick={() => listDocuments(collectionId)}>
          {t('documentList.loadFailedRetry')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={`skeleton-${i.toString()}`}
            className="flex items-center gap-4 rounded-md border border-border p-4"
          >
            <Skeleton className="h-5 w-5" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-1/4" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t('documentList.searchPlaceholder')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="pl-9"
        />
      </div>

      {filteredDocuments.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-16">
          <FileText className="h-12 w-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {isSearching
              ? t('documentList.noMatchSearch', { query: searchQuery })
              : emptyScopeActive
                ? t('documentList.emptyScope')
                : activeTags && activeTags.length > 0
                  ? t('documentList.noMatchTags')
                  : t('documentList.emptyLibrary')}
          </p>
        </div>
      )}

      {filteredDocuments.length > 0 && (
        <div className="space-y-2">
          {filteredDocuments.map((doc) => (
            <div
              key={doc.documentId}
              className="flex flex-col gap-2 rounded-md border border-border p-3 sm:p-4"
            >
              <div className="flex min-w-0 items-start gap-3">
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div
                className="min-w-0 flex-1 cursor-pointer group rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                role="button"
                tabIndex={0}
                onClick={() => openDocumentViewer(doc.documentId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDocumentViewer(doc.documentId);
                  }
                }}
                aria-label={t('documentList.viewAria', { name: doc.title || doc.filename })}
              >
                <p className="truncate text-sm font-medium text-foreground group-hover:text-accent transition-colors duration-150">
                  {doc.title || doc.filename}
                </p>
                <p className="text-xs text-muted-foreground">
                  {doc.filename} &middot; {formatBytes(doc.sizeBytes)}{' '}
                  {doc.createdAt ? `\u00b7 ${formatDate(doc.createdAt)}` : ''}
                </p>
                {/* conversionEmpty rides the same `failure_reason` wire field as
                    `failed`, so the reason is invisible unless this gate admits
                    it — and it is toned `warning`, not `destructive`, because
                    nothing errored. Pattern: DiagramTile.tsx. */}
                {isActionableFailure(doc.indexingStatus) && doc.failureReason && (
                  <p
                    className={cn(
                      'text-xs mt-1',
                      isConversionEmpty(doc.indexingStatus)
                        ? 'text-warning'
                        : 'text-destructive',
                    )}
                  >
                    {doc.failureReason}
                  </p>
                )}
                {/* BILL-06: paid indexing legs skipped for lack of credits. The
                    document IS searchable (keyword/BM25), so warning tone, not
                    destructive. Only on a settled `indexed` row: not on an
                    actionable failure (its own reason line says what went
                    wrong), and not while a re-run is pending/converting. */}
                {offersIndexSkippedRetry(doc.indexingStatus, doc.metadata) && (
                  <p className="text-xs mt-1 text-warning">
                    {t('documentList.indexSkippedCredits')}
                  </p>
                )}
                  {doc.tags && doc.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {doc.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
              {doc.indexingStatus && (
                <Badge
                  data-testid="document-status-badge"
                  variant={indexingStatusVariant(doc.indexingStatus)}
                >
                  {indexingStatusLabel(t, doc.indexingStatus)}
                </Badge>
              )}
              {doc.indexingStatus === 'convertingHiFi' &&
                (() => {
                  const prog = conversionProgress[doc.documentId];
                  return prog ? (
                    <ConversionProgressBar completed={prog.completed} total={prog.total} />
                  ) : null;
                })()}
              {/* Docling Premium: surface which converter ran and, on a
                  fallback, why — and offer the free retry only while the
                  server says its token is live. */}
              <ConverterBadge
                converterUsed={doc.converterUsed ?? ''}
                fallbackCategory={doc.doclingFallbackCategory ?? ''}
                retryAvailable={isDoclingRetryLive(doc.doclingRetryExpiresAt, Date.now())}
                onRetry={() => retryDocling(doc.documentId)}
              />
              {/* conversionEmpty is retryable for the same reason `failed` is:
                  the document is terminal and useless as it stands. BILL-06: an
                  `indexed` document whose paid legs were skipped for credits is
                  retryable too — the server re-arms any status, and a retry
                  counts against the per-user re-arm limit (30/h), so it is
                  offered only while `index_skipped` is set AND the row is
                  settled at `indexed` — never while a re-run is in flight. */}
              {(isActionableFailure(doc.indexingStatus) ||
                offersIndexSkippedRetry(doc.indexingStatus, doc.metadata)) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => retryDocument(doc.documentId)}
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('documentList.retry')}
                </button>
              )}
              {/* Sensitivity marker — labelless to fit the row density.
                  The toggle has aria-label / title on each pill, so AT users
                  still get the full semantics. */}
              <SensitivityToggle
                value={doc.sensitivity ?? Sensitivity.PUBLIC}
                onChange={(v) => updateDocumentSensitivity(doc.documentId, v)}
                hideLabel
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 shrink-0 p-0"
                onClick={() => updateDocumentPinned(doc.documentId, !doc.pinned)}
                aria-label={doc.pinned ? t('documentList.unpinAria', { name: doc.title || doc.filename }) : t('documentList.pinAria', { name: doc.title || doc.filename })}
              >
                {doc.pinned ? <PinOff className="h-4 w-4 text-accent" /> : <Pin className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 shrink-0 p-0"
                onClick={() => deleteDocument(doc.documentId)}
                aria-label={t('documentList.deleteAria', { name: doc.title || doc.filename })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <ManualActionBar surface="document" objectId={doc.documentId} />
              <DocumentActionsMenu
                documentId={doc.documentId}
                label={doc.title || doc.filename}
                tags={doc.tags ?? []}
                knownTags={knownTags}
              />
              </div>
            </div>
          ))}
        </div>
      )}
      <DocumentViewerModal />
    </div>
  );
}

/** Derive all unique tags from the current documents list. */
function useDocumentTags(): string[] {
  const documents = useDocumentsStore((s) => s.documents);
  return React.useMemo(() => {
    const tagSet = new Set<string>();
    documents.forEach((doc) => doc.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [documents]);
}

export { DocumentList, useDocumentTags, indexingStatusVariant };
